import { useCallback, useEffect, useState } from "react";

import {
  CHAIN_METRIC_IDS,
  type ChainMetricId,
  completeMetricOrder,
  defaultMetricIds,
  isChainMetricId,
  isMetricRequired,
  type MetricDropDirection,
  reorderScrollableMetrics,
  SCROLLABLE_METRIC_IDS,
  STICKY_METRIC_IDS,
} from "./optionsChain.columns";
import {
  CHAIN_SIDE_FILTER_OPTIONS,
  type ChainSideFilter,
  DEFAULT_EXPIRATION_COUNT,
  DEFAULT_STRIKE_COUNT,
  EXPIRATION_COUNT_OPTIONS,
  EXPIRATION_FILTER_OPTIONS,
  type ExpirationCount,
  type ExpirationFilter,
  type ExpirationRange,
  STRIKE_COUNT_OPTIONS,
  type StrikeCount,
} from "./optionsChain.types";

export const OPTIONS_CHAIN_PREFERENCES_STORAGE_KEY =
  "atd:options-chain:preferences:v3";

export interface OptionsChainPreferences {
  /** Strikes either side of the money. */
  strikeCount: StrikeCount;
  /**
   * Which columns are enabled. Visibility only — layout follows `metricOrder`.
   */
  metricIds: ChainMetricId[];
  /** Left-to-right order of the scrollable columns. Bid and ask stay pinned. */
  metricOrder: ChainMetricId[];
  sideFilter: ChainSideFilter;
  expirationFilter: ExpirationFilter;
  expirationRange: ExpirationRange;
  expirationCount: ExpirationCount;
}

export interface PreferencesStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const DEFAULT_PREFERENCES: OptionsChainPreferences = {
  strikeCount: DEFAULT_STRIKE_COUNT,
  metricIds: defaultMetricIds(),
  metricOrder: [...SCROLLABLE_METRIC_IDS],
  sideFilter: "both",
  expirationFilter: "all",
  expirationRange: { from: "", to: "" },
  expirationCount: DEFAULT_EXPIRATION_COUNT,
};

const getBrowserStorage = (): PreferencesStorage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const defaults = (): OptionsChainPreferences => ({
  ...DEFAULT_PREFERENCES,
  metricIds: [...DEFAULT_PREFERENCES.metricIds],
  metricOrder: [...DEFAULT_PREFERENCES.metricOrder],
});

/**
 * A stored count, kept when it is one of the menu values and otherwise the
 * default. Legacy typed counts (7, 42, …) snap back so a closed Select never
 * sits on a value it cannot show.
 */
const toListedCount = <T extends number>(
  value: unknown,
  options: readonly T[],
  fallback: T,
): T => options.find((option) => option === value) ?? fallback;

/** Visibility set: keep stored order, always append the pinned bid/ask pair. */
const ensureRequiredMetrics = (
  ids: Iterable<ChainMetricId>,
): ChainMetricId[] => {
  const seen = new Set<ChainMetricId>();
  const next: ChainMetricId[] = [];
  for (const id of ids) {
    if (!isChainMetricId(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    next.push(id);
  }
  for (const id of STICKY_METRIC_IDS) {
    if (!seen.has(id)) {
      next.push(id);
    }
  }
  return next;
};

/**
 * Stored preferences are user input, so every field is validated rather than
 * trusted. An unknown metric id, an empty list or a stale strike count all
 * degrade to the default instead of rendering an empty grid.
 *
 * The selected expiration is deliberately not persisted: it expires, and it is
 * meaningless on a different underlying.
 */
export const sanitizePreferences = (
  value: unknown,
): OptionsChainPreferences => {
  if (!value || typeof value !== "object") {
    return defaults();
  }
  const candidate = value as Partial<
    Record<keyof OptionsChainPreferences, unknown>
  >;

  const strikeCount = toListedCount(
    candidate.strikeCount,
    STRIKE_COUNT_OPTIONS,
    DEFAULT_PREFERENCES.strikeCount,
  );

  const sideFilter = (CHAIN_SIDE_FILTER_OPTIONS as readonly unknown[]).includes(
    candidate.sideFilter,
  )
    ? (candidate.sideFilter as ChainSideFilter)
    : DEFAULT_PREFERENCES.sideFilter;

  const stored = Array.isArray(candidate.metricIds)
    ? candidate.metricIds.filter(isChainMetricId)
    : [];
  const storedOrder = Array.isArray(candidate.metricOrder)
    ? candidate.metricOrder.filter(isChainMetricId)
    : [];

  const expirationFilter = (
    EXPIRATION_FILTER_OPTIONS as readonly unknown[]
  ).includes(candidate.expirationFilter)
    ? (candidate.expirationFilter as ExpirationFilter)
    : DEFAULT_PREFERENCES.expirationFilter;

  const storedRange = candidate.expirationRange as
    | Partial<ExpirationRange>
    | undefined;
  const isDate = (value: unknown): value is string =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const expirationRange: ExpirationRange = {
    from: isDate(storedRange?.from) ? storedRange.from : "",
    to: isDate(storedRange?.to) ? storedRange.to : "",
  };

  const expirationCount = toListedCount(
    candidate.expirationCount,
    EXPIRATION_COUNT_OPTIONS,
    DEFAULT_PREFERENCES.expirationCount,
  );

  return {
    strikeCount,
    sideFilter,
    expirationFilter,
    expirationRange,
    expirationCount,
    metricIds:
      stored.length > 0 ? ensureRequiredMetrics(stored) : defaults().metricIds,
    metricOrder: completeMetricOrder(storedOrder),
  };
};

export const loadPreferences = (
  storage: PreferencesStorage | null = getBrowserStorage(),
): OptionsChainPreferences => {
  if (!storage) {
    return defaults();
  }
  try {
    const stored = storage.getItem(OPTIONS_CHAIN_PREFERENCES_STORAGE_KEY);
    return stored === null
      ? defaults()
      : sanitizePreferences(JSON.parse(stored));
  } catch {
    return defaults();
  }
};

export const persistPreferences = (
  preferences: OptionsChainPreferences,
  storage: PreferencesStorage | null = getBrowserStorage(),
): boolean => {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(
      OPTIONS_CHAIN_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizePreferences(preferences)),
    );
    return true;
  } catch {
    return false;
  }
};

/**
 * Type-level preferences, following the Orders columns pattern: one key for the
 * widget kind rather than per instance, because a user sets column choices once.
 */
export const useOptionsChainPreferences = (
  storage: PreferencesStorage | null = getBrowserStorage(),
) => {
  const [preferences, setPreferences] = useState<OptionsChainPreferences>(() =>
    loadPreferences(storage),
  );

  useEffect(() => {
    persistPreferences(preferences, storage);
  }, [preferences, storage]);

  const setStrikeCount = useCallback((strikeCount: StrikeCount) => {
    setPreferences((current) =>
      current.strikeCount === strikeCount
        ? current
        : { ...current, strikeCount },
    );
  }, []);

  const setSideFilter = useCallback((sideFilter: ChainSideFilter) => {
    setPreferences((current) =>
      current.sideFilter === sideFilter ? current : { ...current, sideFilter },
    );
  }, []);

  const setExpirationFilter = useCallback(
    (expirationFilter: ExpirationFilter) => {
      setPreferences((current) =>
        current.expirationFilter === expirationFilter
          ? current
          : { ...current, expirationFilter },
      );
    },
    [],
  );

  const setExpirationRange = useCallback((expirationRange: ExpirationRange) => {
    setPreferences((current) => ({ ...current, expirationRange }));
  }, []);

  const setExpirationCount = useCallback((expirationCount: ExpirationCount) => {
    setPreferences((current) => ({ ...current, expirationCount }));
  }, []);

  const setMetricVisibility = useCallback(
    (ids: readonly ChainMetricId[], isVisible: boolean) => {
      setPreferences((current) => {
        const next = new Set(current.metricIds);
        for (const id of ids) {
          // Bid and ask are the actionable cells and cannot be turned off.
          if (isVisible || isMetricRequired(id)) {
            next.add(id);
          } else {
            next.delete(id);
          }
        }
        return { ...current, metricIds: ensureRequiredMetrics(next) };
      });
    },
    [],
  );

  const selectAllMetrics = useCallback(() => {
    setPreferences((current) => ({
      ...current,
      metricIds: ensureRequiredMetrics(CHAIN_METRIC_IDS),
    }));
  }, []);

  /** Columns only: restores default visibility and order, not the counts. */
  const resetMetrics = useCallback(() => {
    const next = defaults();
    setPreferences((current) => ({
      ...current,
      metricIds: [...next.metricIds],
      metricOrder: [...next.metricOrder],
    }));
  }, []);

  const reorderMetrics = useCallback(
    (
      source: ChainMetricId,
      target: ChainMetricId,
      direction: MetricDropDirection,
    ) => {
      setPreferences((current) => ({
        ...current,
        metricOrder: reorderScrollableMetrics(
          current.metricOrder,
          source,
          target,
          direction,
        ),
      }));
    },
    [],
  );

  return {
    preferences,
    setStrikeCount,
    setSideFilter,
    setExpirationFilter,
    setExpirationRange,
    setExpirationCount,
    setMetricVisibility,
    selectAllMetrics,
    resetMetrics,
    reorderMetrics,
  };
};
